import * as React from 'react';
import autobind from 'autobind-decorator';
import { resolve } from 'inversify-react';
import { observable, computed } from 'mobx';
import { observer } from 'mobx-react';

import { ToastNotifications } from 'src/core/ui/toast';
import { ContextService } from 'src/core/services/context.service';
import { ControllerProps } from 'src/router';
import { ObjectUtils } from 'src/core/utils/object';
import { BrandFormView } from './brand-form.view';
import { BrandDataService } from './brand-form.data';
import { ConfigurationService } from 'src/core/services/configuration.service';

interface BrandFormParams {
	id: string
}

@observer
export class BrandFormController extends React.Component<ControllerProps<BrandFormParams>, any>  {
	@resolve
	protected contextService: ContextService;

	@observable
	protected dataService: BrandDataService;

	@resolve
	protected toast: ToastNotifications;

	@observable
	mode: ControlMode = 'display';

	@resolve
	protected configurationService: ConfigurationService;

	@observable
	defaultLanguaje: string;

	constructor(props) {
		super(props);
		this.state = {
			refresh: false
		}
	}

	@computed
	protected get title() {
		if (this.dataService && this.dataService.item && this.dataService.item.name) {
			return `Brand ${this.dataService.item.name}`;
		}
		return '';
	}

	@autobind
	protected onModeChanged(mode: ControlMode) {
		this.mode = mode;
	}

	@autobind
	protected create() {
		const item = this.dataService.add(this.defaultLanguaje);
		return item;
	}

	@autobind
	public async save() {
		const item = this.dataService.item;
		const errors = await item.validate(true);
		if (errors.length == 0) {
			this.props.setBusy(true);

			this.dataService.save()
				.then(() => {
					this.props.setBusy(false);
					this.onModeChanged('display');
					this.toast.showDataSaved();
					this.props.history.replace(`/general/brands`);
				})
				.catch((error) => {
					this.props.setBusy(false);
					this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
				});
		} else {
			this.toast.showToast('Validation error!', errors.map(e => e.text), 'error');
		}
	}

	@autobind
	public cancel() {
		this.props.history.push('/general/brands');
	}

	componentDidMount() {
		this.dataService = BrandDataService.make(this.contextService);
		this.props.setBusy(true);
		this.configurationService.current().then(res => {
			this.defaultLanguaje = res.defaultLanguage;
			if (this.props.match.params.id == 'new') {
				// Create new
				this.dataService.create(this.create)
					.then(() => {
						this.mode = 'edit';
						this.props.setBusy(false);
					}).catch(error => {
						this.props.setBusy(false);
						this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
					});
			} else {
				// Load entity
				this.dataService.addFilter('id', 'eq', this.props.match.params.id);
				this.dataService.addSorting('id', 'asc', true);
				this.dataService.pageSize = 1;
				this.dataService.load(query => query)
					.catch((error) => {
						this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
					})
					.then(() => {
						this.props.setBusy(false);
						if (!this.dataService.item.dynamicProperties.quoteNumberPrefix)
							this.dataService.item.dynamicProperties.quoteNumberPrefix = null;
					});
			}
		});
	}

	render() {
		if (this.dataService == null || this.dataService.item == null) {
			return null;
		}

		return (
			<BrandFormView title={this.title}
				mode={this.mode}
				onModeChanged={this.onModeChanged}
				cancel={this.cancel}
				save={this.save}
				model={this.dataService.item}
				onLogoChange={this.onLogoChange}
				onFooterChange={this.onFooterChange}
				setBusy={this.props.setBusy}
				contextService={this.contextService}
			/>
		);
	}

	@autobind
	private onLogoChange(ev: React.ChangeEvent<HTMLInputElement>) {
		const file = ev.target.files[0];
		if (file) {
			const contentReader = new FileReader();
			contentReader.onload = (e: Event) => {
				const result: ArrayBuffer = (e.target as any).result;
				this.dataService.item.logo = new Uint8Array(result);
				this.setState({
					refresh: !this.state.refresh
				});
			};
			contentReader.readAsArrayBuffer(file);
		}
	}

	@autobind
	private onFooterChange(ev: React.ChangeEvent<HTMLInputElement>) {
		const file = ev.target.files[0];
		if (file) {
			const contentReader = new FileReader();
			contentReader.onload = (e: Event) => {
				const result: ArrayBuffer = (e.target as any).result;
				this.dataService.item.footer = new Uint8Array(result);
				this.setState({
					refresh: !this.state.refresh
				});
			};
			contentReader.readAsArrayBuffer(file);
		}
	}
}



// WEBPACK FOOTER //
// ./src/app/general/brands/form/brand-form.controller.tsx